<template>

    <div class="section mt-1">
        <div class="sneha-filter-row">
            <select class="sneha-filter-select mal-text" v-model="program_id" @change="loadApplications()">
                <option value="">എല്ലാ പദ്ധതികളും</option>
                <option v-for="p in programs" :key="p.id" :value="p.id">{{ p.title }}</option>
            </select>
            <select v-if="isStateAdmin" class="sneha-filter-select mal-text" v-model="district_id" @change="loadApplications()">
                <option value="">എല്ലാ ജില്ലകളും</option>
                <option v-for="d in districts" :key="d.id" :value="d.id">{{ d.name }}</option>
            </select>
        </div>
    </div>

    <div class="section mt-1" v-if="isStateAdmin">
        <button type="button" class="sneha-export-btn" @click="exportExcel" :disabled="excelDownloading">
            <ion-icon name="download-outline"></ion-icon>
            <span>{{ excelDownloading ? 'എക്സ്പോർട്ട് ചെയ്യുന്നു...' : 'Export Excel' }}</span>
        </button>
    </div>

    <div class="section mt-2">
        <div class="sneha-count-grid">
            <div class="sneha-count-tile is-completed" :class="{ 'is-active': status_bucket === 'completed' }" @click="toggleBucket('completed')">
                <ion-icon name="checkmark-circle-outline" class="sneha-count-icon"></ion-icon>
                <span class="sneha-count-num">{{ counts.completed }}</span>
                <span class="sneha-count-label mal-text">Completed</span>
            </div>
            <div class="sneha-count-tile is-going-on" :class="{ 'is-active': status_bucket === 'going_on' }" @click="toggleBucket('going_on')">
                <ion-icon name="time-outline" class="sneha-count-icon"></ion-icon>
                <span class="sneha-count-num">{{ counts.going_on }}</span>
                <span class="sneha-count-label mal-text">Going On</span>
            </div>
            <div class="sneha-count-tile is-not-started" :class="{ 'is-active': status_bucket === 'not_started' }" @click="toggleBucket('not_started')">
                <ion-icon name="ellipse-outline" class="sneha-count-icon"></ion-icon>
                <span class="sneha-count-num">{{ counts.not_started }}</span>
                <span class="sneha-count-label mal-text">Not Started</span>
            </div>
            <div class="sneha-count-tile is-vo-not-assigned" :class="{ 'is-active': status_bucket === 'vo_not_assigned' }" @click="toggleBucket('vo_not_assigned')">
                <ion-icon name="alert-circle-outline" class="sneha-count-icon"></ion-icon>
                <span class="sneha-count-num">{{ counts.vo_not_assigned }}</span>
                <span class="sneha-count-label mal-text">VO Not Assigned</span>
            </div>
        </div>
    </div>

    <div class="section text-center" v-if="loading">
        <div class="spinner-border text-primary" role="status"></div>
    </div>

    <div class="section pt-2" v-else>
        <p class="sneha-page-note mal-text" v-if="applications.length">
            അപേക്ഷകൾ ({{ applications.length }})
        </p>

        <div v-if="!applications.length" class="sneha-empty-apps mal-text">
            ഇതുവരെ അപേക്ഷകൾ ഇല്ല.
        </div>

        <div v-else class="sneha-app-list">
            <router-link
                v-for="app in applications"
                :key="app.id"
                :to="applicationDetailPath(app.id)"
                class="sneha-app-item"
            >
                <div class="sneha-app-main">
                    <div class="d-flex justify-content-between align-items-start gap-2">
                        <span class="sneha-app-name mal-text">{{ app.name }}</span>
                        <span class="sneha-app-percent" :class="percentClass(app.completion_percent)">{{ app.completion_percent }}%</span>
                    </div>
                    <span v-if="app.application_number" class="sneha-app-number">{{ app.application_number }}</span>
                    <div class="sneha-app-progress-track">
                        <div class="sneha-app-progress-fill" :class="percentClass(app.completion_percent)"
                            :style="{ width: app.completion_percent + '%' }"></div>
                    </div>
                    <span class="sneha-app-program mal-text">{{ app.program_title }}</span>
                    <span class="sneha-app-meta mal-text">{{ app.unit_name }}<span v-if="app.zone_name"> · {{ app.zone_name }}</span><span v-if="app.district_name"> · {{ app.district_name }}</span></span>
                    <span class="sneha-app-meta mal-text" v-if="app.created_at">തീയതി: {{ app.created_at }}</span>
                    <span class="badge mt-1 align-self-start" :class="statusBadgeClass(app.verification_status)">
                        {{ statusLabel(app.verification_status) }}
                    </span>
                    <span v-if="app.interview_status" class="badge mt-1 align-self-start sneha-interview-badge">
                        <ion-icon name="people-circle-outline"></ion-icon> Added to Interview List
                    </span>
                    <span v-if="app.mark_score !== null && app.mark_score !== undefined" class="badge mt-1 align-self-start sneha-mark-badge">
                        <ion-icon name="star-outline"></ion-icon> Mark: {{ app.mark_score }}
                    </span>
                    <div class="sneha-vo-box mal-text" v-if="app.vo_name">
                        <ion-icon name="shield-checkmark-outline"></ion-icon>
                        <span>{{ app.vo_name }}<span v-if="app.vo_district_name"> · {{ app.vo_district_name }}</span><span v-if="app.vo_zone_name"> · {{ app.vo_zone_name }}</span><span v-if="app.vo_mobile"> · {{ app.vo_mobile }}</span></span>
                    </div>
                    <div class="sneha-vo-box sneha-vo-box--none mal-text" v-else>
                        <ion-icon name="alert-circle-outline"></ion-icon>
                        <span>Verification Officer നിയോഗിച്ചിട്ടില്ല</span>
                    </div>
                </div>
                <ion-icon name="chevron-forward-outline" class="sneha-app-chevron"></ion-icon>
            </router-link>
        </div>
    </div>
</template>

<script>
import UserService from "@/services/user.service";

export default {
    name: "SnehaSparshamApplicationStatusReport",
    data() {
        return {
            loading: true,
            applications: [],
            programs: [],
            districts: [],
            program_id: "",
            district_id: "",
            status_bucket: "",
            counts: { completed: 0, going_on: 0, not_started: 0, vo_not_assigned: 0 },
            excelDownloading: false,
        };
    },
    computed: {
        isStateAdmin() {
            return parseInt(this.$route.params.sub_level, 10) === 1;
        },
    },
    mounted() {
        this.$root.updateParent("അപേക്ഷ സ്റ്റാറ്റസ് റിപ്പോർട്ട്");
        this.loadApplications();
    },
    methods: {
        toggleBucket(bucket) {
            this.status_bucket = this.status_bucket === bucket ? "" : bucket;
            this.loadApplications();
        },
        statusLabel(s) {
            return s === 'verified' ? 'Verified'
                : s === 'not_verified' ? 'Not Verified'
                : s === 'edit_requested' ? 'Edit Requested'
                : s === 'resubmitted' ? 'Resubmitted'
                : s === 'in_progress' ? 'In Progress' : 'Pending';
        },
        statusBadgeClass(s) {
            return s === 'verified' ? 'bg-success'
                : s === 'resubmitted' ? 'bg-info'
                : s === 'in_progress' ? 'bg-warning text-dark'
                : s === 'pending' || !s ? 'bg-secondary'
                : 'bg-danger';
        },
        percentClass(percent) {
            return percent >= 100 ? 'is-complete' : percent >= 50 ? 'is-partial' : 'is-low';
        },
        applicationDetailPath(applicationId) {
            return `/org/${this.$route.params.sub_level}/${this.$route.params.id}/sneha-sparsham-application/${applicationId}`;
        },
        loadApplications() {
            const levelId = parseInt(this.$route.params.id, 10);
            const userLevel = parseInt(this.$route.params.sub_level, 10);
            if (!userLevel) {
                this.applications = [];
                this.loading = false;
                return;
            }

            this.loading = true;
            UserService.authPostRequest("get-sneha-sparsham-application-status-report", {
                user_level: userLevel,
                user_level_id: levelId,
                program_id: this.program_id || null,
                district_id: this.district_id || null,
                status_bucket: this.status_bucket || null,
            }).then((res) => {
                if (res.data.status === "success") {
                    this.applications = res.data.data.list || [];
                    this.programs = res.data.data.programs || [];
                    this.districts = res.data.data.districts || [];
                    this.counts = res.data.data.counts || { completed: 0, going_on: 0, not_started: 0, vo_not_assigned: 0 };
                } else {
                    this.applications = [];
                }
            }).catch(() => {
                this.applications = [];
            }).finally(() => {
                this.loading = false;
            });
        },
        async exportExcel() {
            this.excelDownloading = true;
            try {
                const levelId = parseInt(this.$route.params.id, 10);
                const userLevel = parseInt(this.$route.params.sub_level, 10);
                const response = await UserService.authPostRequest("export-sneha-sparsham-application-status-report-excel", {
                    user_level: userLevel,
                    user_level_id: levelId,
                    program_id: this.program_id || null,
                    district_id: this.district_id || null,
                    status_bucket: this.status_bucket || null,
                }, { responseType: "blob" });

                const blob = response.data;
                const contentType = (response.headers && response.headers["content-type"]) || "";
                if (contentType.indexOf("application/json") !== -1) {
                    const text = await new Promise((resolve) => {
                        const reader = new FileReader();
                        reader.onload = () => resolve(reader.result);
                        reader.readAsText(blob);
                    });
                    const json = JSON.parse(text);
                    alert(json.message || "Download failed");
                    return;
                }

                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `sneha-sparsham-application-status-report-${new Date().toISOString().slice(0, 10)}.xlsx`;
                a.click();
                URL.revokeObjectURL(url);
            } catch (err) {
                let msg = "Failed to export Excel";
                if (err && err.response && err.response.data && typeof err.response.data === "object" && !(err.response.data instanceof Blob)) {
                    msg = err.response.data.message || msg;
                } else if (err && err.message) {
                    msg = err.message;
                }
                alert(msg);
            } finally {
                this.excelDownloading = false;
            }
        },
    },
};
</script>

<style scoped>
.sneha-page-note {
    margin: 0 0 14px;
    padding: 0 4px;
    font-size: 0.9rem;
    color: #6c757d;
    line-height: 1.45;
}

.sneha-empty-apps {
    text-align: center;
    padding: 24px 12px;
    color: #6c757d;
    font-size: 0.92rem;
    background: #fff;
    border-radius: 10px;
    border: 1px dashed #ced4da;
}

.sneha-app-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.sneha-app-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 16px;
    text-decoration: none;
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 10px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    transition: background-color 0.15s ease;
}

.sneha-app-item:active {
    background: #f8f9fa;
}

.sneha-app-main {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    flex: 1;
}

.sneha-app-name {
    font-size: 0.98rem;
    font-weight: 600;
    color: #1a418c;
    line-height: 1.35;
    word-break: break-word;
}

.sneha-app-percent {
    flex-shrink: 0;
    font-size: 0.85rem;
    font-weight: 700;
}

.sneha-app-number {
    display: inline-block;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.3px;
    color: #6b7c93;
    background: #f4f7ff;
    border: 1px solid #dde4f5;
    border-radius: 5px;
    padding: 1px 6px;
    margin-top: 2px;
}

.sneha-app-progress-track {
    height: 5px;
    background: #eef0f5;
    border-radius: 5px;
    overflow: hidden;
    margin: 4px 0 2px;
}

.sneha-app-progress-fill {
    height: 100%;
    border-radius: 5px;
}

.is-complete { color: #12a85a; }
.is-partial { color: #d9a441; }
.is-low { color: #d9534f; }
.sneha-app-progress-fill.is-complete { background: #1DCC70; }
.sneha-app-progress-fill.is-partial { background: #f0ad4e; }
.sneha-app-progress-fill.is-low { background: #d9534f; }

.sneha-app-program {
    font-size: 0.86rem;
    color: #6c757d;
    line-height: 1.35;
}

.sneha-app-meta {
    font-size: 0.82rem;
    color: #495057;
    line-height: 1.35;
}

.sneha-app-chevron {
    flex-shrink: 0;
    font-size: 20px;
    color: #adb5bd;
}

.sneha-filter-row {
    display: flex;
    gap: 8px;
}

.sneha-filter-select {
    flex: 1;
    min-width: 0;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    background-color: #fff;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='none' stroke='%2364748b' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round' d='M1 1l5 5 5-5'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 12px center;
    border: 1.5px solid #dbe3ff;
    border-radius: 10px;
    padding: 10px 30px 10px 12px;
    font-size: 13px;
    font-weight: 600;
    color: #27173E;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.sneha-filter-select:focus {
    outline: none;
    border-color: #1a418c;
}

.sneha-export-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    border: none;
    border-radius: 10px;
    padding: 10px 12px;
    background: #12a85a;
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.sneha-export-btn:disabled {
    opacity: 0.7;
}

.sneha-export-btn ion-icon {
    font-size: 16px;
}

.sneha-count-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
}

.sneha-count-tile {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 16px 8px;
    min-height: 96px;
    border-radius: 12px;
    border: 1.5px solid #eef0f5;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    cursor: pointer;
    user-select: none;
    transition: border-color 0.15s ease, background 0.15s ease, transform 0.1s ease;
}

.sneha-count-tile:active {
    transform: scale(0.97);
}

.sneha-count-icon {
    font-size: 18px;
}

.sneha-count-num {
    font-size: 1.4rem;
    font-weight: 800;
    line-height: 1.1;
}

.sneha-count-label {
    font-size: 0.76rem;
    font-weight: 700;
    color: #64748b;
    text-align: center;
    line-height: 1.3;
}

.is-completed .sneha-count-icon, .is-completed .sneha-count-num { color: #12a85a; }
.is-going-on .sneha-count-icon, .is-going-on .sneha-count-num { color: #d9a441; }
.is-not-started .sneha-count-icon, .is-not-started .sneha-count-num { color: #64748b; }
.is-vo-not-assigned .sneha-count-icon, .is-vo-not-assigned .sneha-count-num { color: #d9534f; }

.is-completed.is-active { border-color: #12a85a; background: #f4fdf8; }
.is-going-on.is-active { border-color: #d9a441; background: #fffaf1; }
.is-not-started.is-active { border-color: #94a3b8; background: #f8f9fa; }
.is-vo-not-assigned.is-active { border-color: #d9534f; background: #fdf4f4; }

.sneha-vo-box {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 6px;
    padding: 6px 10px;
    border-radius: 8px;
    background: #eef2ff;
    color: #1a418c;
    font-size: 0.78rem;
    font-weight: 600;
    line-height: 1.35;
}

.sneha-vo-box ion-icon {
    flex-shrink: 0;
    font-size: 15px;
}

.sneha-vo-box--none {
    background: #fdf4f4;
    color: #d9534f;
}

.sneha-interview-badge {
    background: #c26a00;
    display: inline-flex;
    align-items: center;
    gap: 3px;
}

.sneha-interview-badge ion-icon {
    font-size: 13px;
}

.sneha-mark-badge {
    background: #1a418c;
    display: inline-flex;
    align-items: center;
    gap: 3px;
}

.sneha-mark-badge ion-icon {
    font-size: 13px;
}
</style>
